import { Fragment, useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { api, formatErr, formatPrice } from "@/lib/api";

const STATUSES = [
  { v: "all", label: "Tous" },
  { v: "nouveau", label: "Nouveau" },
  { v: "rappele", label: "Rappelé" },
  { v: "converti", label: "Converti" },
  { v: "perdu", label: "Perdu" },
];

export default function AdminQuotes() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("all");
  const [expanded, setExpanded] = useState(null);

  const load = () =>
    api
      .get("/admin/quotes", { params: status !== "all" ? { status } : {} })
      .then((r) => setRows(r.data))
      .catch(() => {});

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const updateStatus = async (id, newStatus) => {
    try {
      await api.patch(`/admin/quotes/${id}`, { status: newStatus });
      toast.success("Statut mis à jour");
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce devis ?")) return;
    try { await api.delete(`/admin/quotes/${id}`); load(); toast.success("Supprimé"); }
    catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div>
      <div className="font-heading text-3xl font-black uppercase tracking-tight">Devis simulés</div>
      <div className="h-1 w-16 bg-brand mt-2 mb-6" />
      <p className="text-zinc-400 text-sm mb-4">Chaque utilisation du simulateur de prix avec contact saisi est enregistrée ici.</p>

      <div className="flex flex-wrap gap-2 mb-4">
        {STATUSES.map((s) => (
          <button key={s.v} onClick={() => setStatus(s.v)}
            className={`px-3 py-2 text-xs font-bold uppercase tracking-wider border ${status === s.v ? "bg-brand text-white border-brand" : "border-ink-700 text-zinc-400 hover:border-brand"}`}>
            {s.label}
          </button>
        ))}
      </div>

      <div className="bg-ink-900 border border-ink-700 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-ink-800 text-zinc-400 uppercase text-xs tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">Date</th>
              <th className="text-left px-4 py-3">Service</th>
              <th className="text-left px-4 py-3">Client</th>
              <th className="text-left px-4 py-3">Tél.</th>
              <th className="text-right px-4 py-3">Estimé</th>
              <th className="text-left px-4 py-3">Statut</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-700">
            {rows.map((r) => (
              <Fragment key={r.id}>
                <tr className="hover:bg-ink-800 cursor-pointer" onClick={() => setExpanded(expanded === r.id ? null : r.id)}>
                  <td className="px-4 py-3 text-zinc-300">{(r.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="px-4 py-3">{r.service_type}</td>
                  <td className="px-4 py-3">{r.customer_name || <span className="text-zinc-600">—</span>}</td>
                  <td className="px-4 py-3">{r.customer_phone || <span className="text-zinc-600">—</span>}</td>
                  <td className="px-4 py-3 text-right font-bold">{formatPrice(r.estimated_price)}</td>
                  <td className="px-4 py-3">
                    <select
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => updateStatus(r.id, e.target.value)}
                      value={r.status || "nouveau"}
                      className="bg-ink-800 border border-ink-700 px-2 py-1 text-xs"
                    >
                      {STATUSES.filter((s) => s.v !== "all").map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={(e) => { e.stopPropagation(); remove(r.id); }} className="text-zinc-500 hover:text-red-500"><Trash2 size={14} /></button>
                  </td>
                </tr>
                {expanded === r.id && (
                  <tr className="bg-ink-950">
                    <td colSpan={7} className="px-6 py-4 text-sm">
                      <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
                        <div><span className="text-zinc-500">Email :</span> {r.customer_email || "—"}</div>
                        <div><span className="text-zinc-500">Véhicule :</span> {r.vehicle_type || "—"}</div>
                        <div><span className="text-zinc-500">Adresse départ :</span> {r.pickup_address || "—"}</div>
                        <div><span className="text-zinc-500">Adresse arrivée :</span> {r.dropoff_address || "—"}</div>
                        <div><span className="text-zinc-500">Distance :</span> {r.distance_km} km</div>
                        <div><span className="text-zinc-500">Options :</span> {[r.night && "nuit", r.sunday && "dimanche", r.accident && "accident", r.difficult_access && "accès difficile", r.non_running && "non roulant"].filter(Boolean).join(", ") || "—"}</div>
                      </div>
                      {r.breakdown?.length && (
                        <div className="mt-3 bg-ink-900 border border-ink-700 p-3">
                          <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Détail du calcul</div>
                          <ul className="text-xs space-y-1">
                            {r.breakdown.map((b, i) => (
                              <li key={i} className="flex justify-between"><span>{b.label}</span><span className="font-mono">{formatPrice(b.amount)}</span></li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
            {!rows.length && <tr><td colSpan={7} className="text-center py-8 text-zinc-500">Aucun devis</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
